import createInstance from "../util";
import { OriginalUserInfo, UserPreferenceSetting } from "../../store/data/User";
import { LanguageList } from "./model";
import { isNil } from "lodash-es";
import { OrderTriggerTypeEnum } from "../../constants/order";

export interface BaseResponse<T> {
  code: string;
  data: T;
  msg?: string;
  errorData?: {
    action: string;
    cipher: string;
  };
}

export interface UserData {
  lastLogin: {
    date: string;
    ip: string;
    region: string;
  };
  loginName: string;
  msgCountDownInterval: number;
  kycUpdate: boolean;
  settings: {
    mbf: number; // mobile-bind-flag 绑定手机
    ebf: number; // email-bind-flag 绑定邮件
    gbf: number; // google-bind-flag 谷歌绑定
    tbf: number; // 资金密码绑定
    tpi: number; // 资金密码验证规则 1，2，3
    tpl: number; // fundDisabled
    kf: number; // 2 表示有实名，0-无认证 1-一级认证 2-高级认证
    oo: number; // 是否开通OTC
    oc: number; // 是否开通合约
    ob: number; // 是否开启BFT抵扣
    eef: number; // emailEnable
    mef: number; // phoneEnable
    gef: number; // googleEnable
    k1f: number; // kyc认证等级1 (0:无认证 1:审核中 2:审核通过 3:审核失败)
    k2f: number; // kyc认证等级2 (0:无认证 1:审核中 2:审核通过 3:审核失败)
    pim: number; // PIN码总开关
    pis: number; // PIN码交易开关
  };
  userInfo: OriginalUserInfo;
  userName: string; // 2023-08-29 新增
}

export interface ContractList {
  contractList: ContractInfo[];
}

//获取usdt 折合 汇率信息
export type ratesType = {
  USDT_USD: string;
  usdt_cny: string;
  usdt_jpy: string;
  usdt_krw: string;
  usdt_twd: string;
  usdt_rub: string;
  usdt_eur: string;
  usdt_try: string;
  usdt_vnd: string;
  usdt_IDR: string;
  usdt_php: string;
  usdt_hkd: string;
  usdt_thb: string;
};

export type CurrencyData = {
  rates: ratesType;
};

let instance2 = createInstance("v1/user");
let commonInstance = createInstance("");

export async function getCurrency(): Promise<BaseResponse<CurrencyData>> {
  return instance2.post("public/usdtRate");
}

/**
 * 获取用户信息
 */
export function getUserInfo(): Promise<BaseResponse<UserData>> {
  return instance2.post("overview/userinfo");
}

/****
 * 后端清除cookie
 */
export const invokeClearCookie = () => {
  return instance2.post("public/clearCookie");
};

/****
 * 删除服务器的信息
 */
export const loginLogout = () => {
  return instance2.post("/login/logout");
};

/**
 * 切换到模拟盘
 */
export function switchToSimulation(): Promise<
  BaseResponse<Record<string, never>>
> {
  return instance2.post("paptrading/switch");
}

/**
 * 切换到实盘
 */
export function switchToReal(): Promise<BaseResponse<Record<string, never>>> {
  return instance2.post("paptrading/switchReal");
}

interface BigFamilyInfo {
  bigFamilyQrImgUrl: string;
  officialServiceWeChat: string;
  officialWechat: string;
}

export function getBigFamilyQrImg(): Promise<BaseResponse<BigFamilyInfo>> {
  return commonInstance.post("v1/if/base/getBigFamilyQrImg");
}

/**
 * 获取系统时间
 */
export function getCurrentTime(): Promise<BaseResponse<{ t: number }>> {
  return commonInstance.post("v1/mix/public/time");
}

export type FinanceTotalCapital = {
  coinCapitalBtc: string;
  coinCapitalUsdt: string;
  contractCapitalBtc: string;
  contractCapitalUsdt: string;
  otcCapitalBtc: string;
  otcCapitalUsdt: string;
  totalCapitalBtc: string;
  totalCapitalUsdt: string;
};

/**
 * 获取总资产概览
 */
export function financeTotalCapital(): Promise<
  BaseResponse<FinanceTotalCapital>
> {
  return commonInstance.post("v1/if/finance/totalCapital");
}

let mcpAssetInstance = createInstance("v1/mcp");
export type McpCapital = {
  businessLine: number;
  contractTotalBTC: string;
  contractTotalUSDT: string;
};

//专业合约资产
export function mcpCapital(): Promise<BaseResponse<McpCapital[]>> {
  return mcpAssetInstance.post("account/sum/list");
}

export type TotalAssetsOverview = {
  contractTotalBtc: string;
  contractTotalUsdt: string;
  totalBtc: string;
  totalUsdt: string;
  spot?: {
    balanceList: any[];
    totalBtc: string;
    totalUsdt: string;
  };
  professionalContract?: {
    totalBtc: string;
    totalUsdt: string;
  };
  mixContractAssets?: {
    totalBtc: string;
    totalUsdt: string;
  };
  ct?: {
    contractTotalBtc: string;
    contractTotalUsdt: string;
  };
};

export function totalAssets(params: {
  bizLineCodes: string[];
}): Promise<BaseResponse<TotalAssetsOverview>> {
  return commonInstance.post("v1/mix/assetsV2");
}

//按照bizLineCodes 传值取对应账户数据
export function totalAssetType(
  params
): Promise<BaseResponse<TotalAssetsOverview>> {
  return commonInstance.post("v1/mix/assetsV2", params);
}

type feeConfigType = {
  taker: string;
  maker: string;
};

//获取合约费率配置
export function queryFeeRateBySymbolId(data: {
  symbolId: string;
}): Promise<BaseResponse<feeConfigType>> {
  return commonInstance.post("v1/mix/queryFeeRateBySymbolId", data);
}

export type UserFavorite = {
  coinName: string;
  chooseList?: Array<{
    symbolId: string;
    symbolCode: string;
    businessLine: number;
  }>;
};

/**
 * 须登录：查询用户自选
 * 查询参数：businessLine:1币币，10专业合约，11混合合约
 */
export function indexUserSelectedList(params?: {
  businessLine?: number;
}): Promise<BaseResponse<UserFavorite>> {
  // return commonInstance.post('v1/mix/index/userSelectedList', data)
  // api: https://yapi.sniper5.vip/project/23/interface/api/70402
  return commonInstance
    .post<BaseResponse<UserFavorite>>("v1/mix/index/userGroupSelectedList", {
      type: 1,
    })
    .then((res) => {
      if (isNil(params?.businessLine)) return res;
      return {
        ...res,
        data: {
          ...res.data,
          chooseList: res.data?.chooseList?.filter(
            (item) => item.businessLine === params.businessLine
          ),
        },
      };
    });
}

/**
 * 须登录：添加用户自选
 * 参数：businessLine:1币币，10专业合约，11混合合约
 * symbolId
 * @param data
 * @returns {Promise<{}>}
 */
export function addUserSelf(data: {
  businessLine: number;
  symbolId: string;
}): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/user/optional/addUserSelf", data);
}

/**
 * 须登录：移除用户自选
 * @param data
 * @returns {Promise<{}>}
 */
export function deleteUserSelf(data: {
  businessLine: number;
  symbolId: string;
}): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/user/optional/deleteUserSelf", data);
}

/**
 * 须登录：更新热搜
 * @param data
 * @returns {Promise<{}>}
 */
export function updateHotSearchNum(data: {
  searchNum: number;
  symbolId: string;
}): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mix/index/updateNum", data);
}

/**
 * 获取用户偏好设置
 *
 * 接口层做个map 兼容不同端的差异 因为tpslPriceType（OrderTriggerTypeEnum，MARKET = 1, FAIR = 2）跟profitOrLossPriceType（0：最新，1:标记） 对不上
 *
 * @returns {Promise<{}>}
 */

const profitOrLossPriceTypeMap = {
  0: OrderTriggerTypeEnum.MARKET,
  1: OrderTriggerTypeEnum.FAIR,
};
const tpslPriceTypeMap = {
  [OrderTriggerTypeEnum.MARKET]: 0,
  [OrderTriggerTypeEnum.FAIR]: 1,
};

export function getUserPreference(): Promise<
  BaseResponse<UserPreferenceSetting>
> {
  return commonInstance.post("v1/user/preference/get").then((res) => {
    try {
      if (res?.data) {
        res.data.tpslPriceType =
          profitOrLossPriceTypeMap[res?.data?.profitOrLossPriceType];
        delete res.data.profitOrLossPriceType;
      }
    } catch (e) {
      console.error("getUserPreference", e);
    }
    return res;
  });
}

/**
 * 设置用户偏好设置-合约专属
 * @param data
 * @returns {Promise<{}>}
 */
export function setUserPreference(
  data: Partial<UserPreferenceSetting>
): Promise<BaseResponse<any>> {
  const assignData = { ...data };
  if (assignData?.tpslPriceType) {
    assignData.profitOrLossPriceType =
      tpslPriceTypeMap[assignData.tpslPriceType];
    delete assignData.tpslPriceType;
  }
  return commonInstance.post("v1/user/preference/set", assignData);
}

export type ImportNoticeType = {
  webPage: number; // //1全局，2首页，3现货，4合约，5专业合约，6 OTC，7海外OTC，8跟单，9资产
  startTime: string;
  endTime: string;
  id: number;
  substance: string;
  noticeUrl: string;
};

export type ImportanceNotificationInfo = {
  id: string;
  content: string; // 必须是完整连接，或者带语言参数的部分url。http://xxx.xxx/xxx/xxx 或 /en/something
  name: string;
  title: string;
  updateTime: number;
  hasRead?: boolean;
};

export type ImportanceNotificationSet = {
  faqRecharge: ImportNoticeType[];
  faqWithdrawal: ImportNoticeType[];
  importantNotices: ImportNoticeType[];
  riskReminder: ImportNoticeType[];
  spotsNotice: ImportanceNotificationInfo[];
  strategyNotice: ImportNoticeType[];
};

/**
 * 网站重要通知
 * @returns {Promise<{}>}
 */
export function getImportNotice(): Promise<
  BaseResponse<ImportanceNotificationSet>
> {
  return commonInstance.post("v1/mix/index/importance");
}

// type webCommonConfigType = {
//     //跟单开关
//     followSwitch: boolean
//     //合约开关
//     contractSwitch: boolean
//     //fiat买币
//     fiatSwitch: boolean
// }

/****
 * 获取 老合约是否展示的 配置
 * 原来是app 使用的 加到了 app初始化接口中了 所以用到app初始化接口 只用到里面个别字段
 * 临时代码 当老合约都下线之后,不会再用到
 */
export function webCommonConfig(): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mix/public/webCommonConfig");
}

/****
 * 获取 footer 社区配置
 */
export function getFooterInfo(): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mix/index/footer");
}

/****
 * 获取 支持的语言列表
 */
export function getLanguageList(): Promise<BaseResponse<LanguageList>> {
  return commonInstance.post("v1/mix/public/languageTypeList");
}

/**
 * 语言与其对应的货币
 * 如：key="en_US"，value="USD"
 * 注意：value可能为小写字母，与SupportedCurrency中的name字段对应
 */
export type CountryCurrencyMap = Record<string, string>;

/**
 * 货币汇率
 * 如：key="usdt_try"，value="19.4759"
 * 注意：key中的两个货币名称，均为小写，与SupportedCurrency中的value字段对应
 */
export type CurrencyRateMap = Record<string, string>;

export type SupportedCurrency = {
  name: string; // 名称，如USD，注意，该字段可能为小写字母。
  symbol: string; // 符号，如¥，$等
  value: string; // 唯一键，主要是用于从另外一个map中取映射值
  iconUrl: string; // 图标url
};

export type SupportedCurrencyInfo = {
  country: CountryCurrencyMap;
  rates: CurrencyRateMap;
  supported: SupportedCurrency[];
};

/****
 * 获取 支持的费率
 */
export function getRateList(): Promise<BaseResponse<SupportedCurrencyInfo>> {
  return commonInstance.post("v1/mix/public/usdtRate");
}

/**
 * 修改持仓模式positionModeSet
 * yapi地址：https://yapi-upex.go.akamai-access.com/project/32/interface/api/7069
 */
type positionModeSetData = {
  holdMode: number;
  userId: number;
  businessLine: number;
  symbolId: string;
  tokenId: string;
  secondBusinessLine: string;
};

export function setPositionModeFunc(
  params: Partial<positionModeSetData>
): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mcp/adjust/position/mode", params);
}

/*
 * 获取是否有充值记录
 */
export function getHasRecharge(): Promise<BaseResponse<boolean>> {
  return commonInstance.post("v1/mix/index/hasRecharge");
}

/****
 * 获取 合规弹框提示
 */
export function getTipsList(): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mix/base/tipsList");
}

/**
 * 可用币种对应链配置 现货用
 * @returns
 */
// export function getAllBitIconInfo(): Promise<BaseResponse<any>> {
//     return commonInstance.post('v1/spot/public/coinChainList')
// }
/**
 * 可用币种对应链配置 全部币币信息
 * @returns
 */
export function getContractBitIconInfo(): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mix/public/getCoinConfigList");
}

/**
 * 获取深度合并支持档位
 * @param data
 */
export function platePrice(data: {
  businessType: number;
  md5?: string;
}): Promise<
  BaseResponse<{
    md5Value: string;
    value: Array<{
      symbolId: string;
      priceScaleGearList: string[];
      businessLine: number;
    }>;
  }>
> {
  return commonInstance.get("v1/mix/public/getPlatePrice", data);
}

export type EtfBaseInfo = {
  coinName: string;
  iconUrl: string;
  leverageRatio: number;
  productDesc: string;
  productDescContent: string;
  productName: string;
  publishProductCode: string;
  startDate: number;
  cardinalUnit: string;
  cardinalNumber: string;
};

/**
 * 获取ETF介绍
 * @param data
 */
export function getEtfBaseInfo(data: {
  symbolId: string;
}): Promise<BaseResponse<EtfBaseInfo>> {
  return commonInstance.post("v1/mix/public/getEtfBaseInfo", data);
}

export type CoinAboutParams = {
  symbolId?: string; //合约
  baseCoinId?: string; //现货
  languageId?: number;
};

export type CoinAboutChainInfo = {
  chainFullName: string;
  chainShortName: string;
  contractAddress: string;
};

export type CoinAboutInvestorInfo = {
  iconUrl: string;
  name: string;
};

export type CoinAboutSectionInfo = {
  sectionId: string;
  sectionName: string;
};

export interface CoinAboutData {
  pageDisplay?: number; //1 显示 0 隐藏 是否显示币种概况
  webFeedback?: number; //1 显示 0 隐藏 //是否显示反馈
  appFeedback?: number;
  bgSymbol?: string;
  bgCoinName?: string;
  marketValue?: number;
  fullyDilutedMarketCap?: number;
  marketShare?: number;
  holdingCoins?: number;
  high?: number;
  low?: number;
  dateLaunched?: string | number;
  volume24?: number;
  circulatingSupply?: number;
  maxSupply?: number;
  totalSupply?: number;
  website?: Array<string>;
  technicalDoc?: Array<string>;
  twitter?: Array<string>;
  sourceCode?: Array<string>;
  reddit?: Array<string>;
  title?: string;
  introduction?: string;
  explorerList?: Array<string>;
  webDisplayCoinInfo?: boolean;
  investorsList?: Array<CoinAboutInvestorInfo>;
  configChainInfoList: Array<CoinAboutChainInfo>;
  facebook: string[];
  facebookStr: string;
  telegram: string[];
  telegramStr: string;
  discord: string[];
  discordStr: string;
  sectionInfoList: CoinAboutSectionInfo[];
}

export interface CoinAboutEconomicsData {
  bgSymbol?: string;
  languageCodes?: Array<number>;
  coinSymbol?: Array<string>;
  coinsRelation?: Array<{
    relationSymbol: string;
  }>;
  maxSupply?: number;
  bgCoinName?: string;
  appFeedback?: number;
  webFeedback?: number;
  webDisplayCoinEco?: boolean;
  isVisible?: number;
  coinEcoExtDescriptions?: {
    languageCode?: number;
    usageDescH5?: string;
    governanceDescH5?: string;
    governanceDesc?: string;
    id?: string;
    uniqueDescH5?: string;
    usageDesc?: string;
    uniqueDesc?: string;
    distributions?: Array<{
      holder: string;
      percentage: number;
    }>;
  };
}

/**
 * 获取币种概况
 * @param data
 */
export function getCoinAboutInfo(
  data: CoinAboutParams
): Promise<BaseResponse<CoinAboutData>> {
  return commonInstance.post("v1/cms/coin/getCoinInformation", data);
}

/**
 * 获取代币情况
 * @param data
 */
export function getCoinAboutEconomics(
  data: CoinAboutParams
): Promise<BaseResponse<CoinAboutEconomicsData>> {
  return commonInstance.post("v1/cms/economics/query", data);
}

export type CoinAboutDisplayConfig = {
  showTab: boolean; // 是否展示项目公告
  showTwitter: boolean; // 是否展示twitter
  showBlog: boolean; // 是否展示博客
  coinBaseInfo: boolean; // 是否展示基本资料
  coinEco: boolean; // 是否展示代币经济学
  displayPosition: boolean; // 是否展示持仓分布
  kNewsTab: boolean; // 是否展示news tab
};

export function createDefaultCoinAboutDisplayConfig(): CoinAboutDisplayConfig {
  return {
    displayPosition: false,
    kNewsTab: false,
    coinBaseInfo: false,
    coinEco: false,
    showBlog: false,
    showTab: false,
    showTwitter: false,
  };
}

/**
 * 获取巴中介绍各种tab控制
 * @param symbolId
 */
export function cmsCoinConfigDisplay(
  symbolId: string
): Promise<BaseResponse<CoinAboutDisplayConfig>> {
  return commonInstance.post("v1/cms/coin/config/display", { symbolId });
}

// 持仓分布查询Top10持币地址
export function coinAboutTop10Address(symbolId: string): Promise<
  BaseResponse<{
    list: Array<{ address: string; percent: string; balance: string }>;
  }>
> {
  return commonInstance.post("v1/cms/position/web/holdingAddress", {
    symbolId,
  });
}

// 持仓分布查询24h活跃地址
export function coinAbout24hActiveAddress(symbolId: string): Promise<
  BaseResponse<{
    allBuy: string;
    allSell: string;
    list: Array<{ sell: string; buy: string; time: string; ts: string }>;
  }>
> {
  return commonInstance.post("v1/cms/position/web/activeAddress", { symbolId });
}

export type CoinAboutBlogAndTwitterParams = {
  symbolId: string;
  announceType: number; // 1 twitter 2 博客 3 twitter和博客
  lastTime?: string; // 最后一条记录的发布时间，初次传空
  pageSize: number; // 分页大小，最大10
  languageId?: number; // 语言id
};

export type CoinAboutBlogData = {
  id: string;
  title: string;
  contentAbs: string; // 摘要
  content: string; // 正文
  updateTime: string;
  pubTime: string;
  detailUrl: string; // 详情地址
};

export type CoinAboutTwitterData = {
  id: string;
  content: string;
  originUrl: string;
  pics: string[];
  updateTime: string;
  pubTime: string;
};

export type CoinAboutBlogAndTwitterRes = {
  hasNextPage: boolean;
  lastTime: string;
  announceList: Array<{
    id: string;
    announceType: string; // 1-twitter 2-blog
    pubTime: string;
    updateTime: string;
    languageId: number;
    twitter?: CoinAboutTwitterData;
    blog?: CoinAboutBlogData;
  }>;
};

/**
 * 获取项目公告列表
 * @param params
 */
export function cmsAnnounceList(
  params: CoinAboutBlogAndTwitterParams
): Promise<BaseResponse<CoinAboutBlogAndTwitterRes>> {
  return commonInstance.post("v1/cms/announce/list", params);
}

export type TranslationData = {
  content: string;
  title: string;
  contentAbs: string;
};

export const EmptyTranslationData: TranslationData = {
  content: "",
  title: "",
  contentAbs: "",
};

/**
 * 翻译文章
 * @param params
 */
export function cmsAnnounceTranslation(params: {
  toLanguageId: string;
  fromLanguageId: string; // 对应文章详情详情中的languageId
  id: number; // 文章id
  announceType: number; // 1-twitter 2-blog
  updateTime: number;
}): Promise<BaseResponse<TranslationData>> {
  return commonInstance.post("v1/cms/announce/translation", params);
}

/**
 * 根据业务线获取公告列表
 * 合约 现货/杠杠
 * 2合约 3现货 bizType对接人: @Grech Guo
 */
export function cmsNotificationList(params: {
  newestAnnounceBizType: 2 | 3 | 4 | 5;
  languageId: string;
}): Promise<
  BaseResponse<
    {
      contentId: string;
      content: string;
      showTime: string;
      title: string;
      jumpUrl: string;
      sectionId: string;
    }[]
  >
> {
  return commonInstance.post(
    "v1/cms/helpCenter/content/newest/announce/list",
    params
  );
}

/**
 * 根据业务线获取公告列表
 * 合约 现货/杠杠
 * 2合约 3现货 bizType对接人: @Grech Guo
 */
export function cmsNotificationRead(params: { contentId: number }): Promise<
  BaseResponse<{
    data: number;
  }>
> {
  return commonInstance.post(
    "v1/cms/helpCenter/content/newest/announce/recordRead",
    params
  );
}
// 可用的域名列表
export function getDomainConfigList() {
  return commonInstance.get("v1/mix/system/getDomainConfigList");
}

/**
 * https://yapi.cointr.tools:8451/project/23/interface/api/79031
 * 查询 指数价格的数据源列表
 * */

export type symbolSiteNameData = {
  symbolId: string;
  symbolSiteNameMap: {
    [key: string]: string[];
  };
};
export function getSiteNameList(data: {
  symbolId: string;
}): Promise<BaseResponse<symbolSiteNameData>> {
  return commonInstance.post("v1/mix/public/getSiteNameList", data);
}

/**
 * 拉取合约总览页 banner 列表
 */

export type BannerItemType = {
  imgUrl: string;
  jumpUrl: string;
  title: string;
  imageUrl?: string;
  hotActivityStartTime?: string;
  hotActivityEndTime?: string;
  secondTitle?: string;
  sortid?: string;
  hotActivityId?: string;
  location?: number;
};
export type GuideActType = BaseResponse<Array<BannerItemType>>;
export function fetchFutureHotBanners({
  params,
  search,
}: any): Promise<GuideActType> {
  let appTheme = "dark";
  const [perfix, queryStr] = search?.split("?") || ["", ""];
  if (queryStr) {
    const queryArr = queryStr?.split("&");
    queryArr.forEach((item) => {
      if (/appTheme/.test(item)) {
        appTheme = item.split("=")[1];
      }
    });
  }

  return commonInstance.post(
    "v1/mix/index/locationBanner",
    {
      locale: params.locales,
      bannerTerminalType: 1,
      locations: [83, 86],
    },
    { appTheme }
  );
}

export interface CmsPublicNewsWebKNewsListProps {
  languageId: number;
  currencyCode: string;
  newsCount?: number;
  minimumShowTime?: number;
}

export interface NewsArticleProps {
  id: string;
  contentId: string;
  title: string;
  abstractContent: string;
  profileImg: string;
  showTime: string;
  author: string;
  sourceName: string;
}

export interface CmsPublicNewsWebKNewsListRes {
  hasNextPage: boolean;
  newsList: Array<NewsArticleProps>;
  recommendNewsList: Array<NewsArticleProps>;
  topNewsList: Array<NewsArticleProps>;
}

/**
 * 获取KNews列表
 * @param params
 */
export function cmsPublicNewsWebKNewsList(
  params: CmsPublicNewsWebKNewsListProps
): Promise<BaseResponse<CmsPublicNewsWebKNewsListRes>> {
  return commonInstance.post("v1/cms/public/news/webKNewsList", params);
}

export interface CmsPublicNewsDetailProps {
  contentId: string;
  languageId: number;
}

export interface CmsPublicNewsDetailRes {
  contentId: string;
  originAuthor: string;
  content: string; // 内容
  title: string; // 标题
  showTime: string;
  sourceName: string; // 来源名称
  siteImg: string;
}

/**
 * 获取KNews详情
 * @param params
 */
export function cmsPublicNewsDetail(
  params: CmsPublicNewsDetailProps
): Promise<BaseResponse<CmsPublicNewsDetailRes>> {
  return commonInstance.post("v1/cms/public/news/detail", params);
}

export interface PinVerifyProps {
  enable: number; // 1 打开 2 关闭 测试用，后期删掉
  type: 1 | 2; // 1 总开关  2 交易开关
}

/**
 * 切换PIN码 交易开关
 * @param data
 * @returns {Promise<{}>}
 */
export function togglePinVerifySwitch(
  data: PinVerifyProps
): Promise<BaseResponse<any>> {
  // return commonInstance.post('v1/user/security/pin/open-pin', data)
  return commonInstance.post("v1/user/security/pin/enable-pin", data);
}
